// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/users/AdminUsersPage.tsx
//
// Phase 1 Task 50 — Admin Users management page.
// Phase 3.4a Wave 9 Task 23 — Sale/CS assignment columns + action button
//   (P3.4a., P3.4a., P3.4a., goal). Fields-only this
//   phase — auto-assign + column-binding-to-merchants land in Phase 10.
// Phase 8.2 Wave 7 Task 49 — Create dialog + self-action guard.
//   •/99/380/474 — Create button + CreateAdminUserDialog (invite-only)
//   • Cannot edit/disable SELF: action buttons disabled when
//                  row.id === currentUser.id.
//   • Error envelope unwrap (user_message > message) handled
//                  inside admin-users.api.ts; the page consumes through the
//                  CreateAdminUserDialog only.
//   • Accountant role exposed in both filter + create-role picker.
//
// Per CONTEXT (RBAC) + (impersonate redirect to merchant subdomain).
// Goals:.
//
// Behavior:
//   - <Table> from @printway/ui pulling GET /api/v1/admin/users.
//   - Filters: roleType (primaryRole) + status + (3.4a) Sale Assigned +
//     CS Assigned dropdown filters. The Sale/CS dropdown filters are
//     mounted as TableFilterField select fields populated from
//     /admin/users?role=saler|support — Phase 3.4a ships the UI surface;
//     the actual server-side filter binding to merchants.{saleAssigned,
//     csAssigned} is wired in Phase 10 once the merchant listing is added.
//   - Row actions: grant role / revoke role (modal w/ role picker),
//     suspend/unsuspend (ConfirmDialog), reset 2FA (ConfirmDialog),
//     impersonate (ImpersonateButton — visible only when allowed),
//     Assign Sale/CS (Phase 3.4a P3.4a., gated by `merchant:manage`).
//   - Header: "Create user" button — visible when `user:manage`
//     or `system:manage_admins` is held. Opens CreateAdminUserDialog.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ImageViewerModal,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Table,
  useAuth,
  type TableColumn,
  type TableFilterField,
  type TableState,
  useToast,
  PageHeader,
} from '@printway/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { apiClient } from '@printway/shared/api';
import { useTranslation } from '../../i18n/useTranslation.js';
import { ImpersonateButton } from './ImpersonateButton.js';
import { CreateAdminUserDialog } from '../../features/users/CreateAdminUserDialog.js';
import { PERMISSIONS } from '../../lib/permissions.js';
import { permissionMatches } from '../../lib/permission-match.js';

// ---------------------------------------------------------------------------
// Wire types — slim mirror of API-CONTRACTS L2685-2706.
// ---------------------------------------------------------------------------

interface AdminUserRow extends Record<string, unknown> {
  id: string;
  email: string;
  displayName: string;
  /**
   * Round-15 (debug 2026-05-23) — avatar URL emitted by BE. Falls back to
   * an initials chip when null/empty.
   */
  avatarUrl?: string | null;
  /**
   * Round-16 (debug 2026-05-23) — surface 2FA enrollment so admins can
   * spot accounts that need a security-policy nudge. BE already returns
   * the field; just bind a column to it.
   */
  twoFactorEnabled?: boolean;
  primaryRole: string;
  /**
   * All granted role enums for this user. BE serializer at
   * `apps/api/src/modules/admin/handlers/adminUsers.handler.ts` returns
   * `roles: row.roles ?? []`. Grant-role/revoke-role mutations modify
   * this array (additive/subtractive) WITHOUT touching `primaryRole`/
   * `roleType` (DB unique constraint on `roleType: 'superadmin'` per
   * Phase 1 Task 33 design). UI must read `roles` to surface mutations.
   *
   * Optional for backwards compat — if BE serializer drops the field
   * (older API), table falls back to `primaryRole` via `??` chain.
   */
  roles?: string[];
  /**
   * Tenant account kind ('merchant' | 'vendor' | 'affiliate') from
   * `User.intendedRole`. Merchant/vendor accounts hold NO global role by design
   * (register.handler.ts writes `roles: []`; grantRole() refuses platform roles
   * on tenant accounts), so `roles`/`primaryRole` are both empty for them and
   * the Role column used to render "—" for every new merchant. Rendered as a
   * distinct chip when no global role exists. dbg-20260730-072612.
   *
   * Optional for backwards compat — absent on older API builds.
   */
  accountType?: 'merchant' | 'vendor' | 'affiliate' | null;
  status: string;
  createdAt: string;
  /**
   * Round-16 iter 2 (debug 2026-05-23) — surface last-modified timestamp
   * so admins can quickly spot stale accounts vs. recently-touched ones.
   * BE already emits `updatedAt: string | null` (ISO 8601).
   */
  updatedAt?: string | null;
  lastLoginAt: string | null;
  // Round-15 (debug 2026-05-23): Sale/CS assignment columns removed from
  // /system/users — those associations live on Merchant docs (joined on
  // /admin/merchants per merchants-list handler) and don't apply to admin
  // staff. The empty '—' cells were noise on this page. The Phase 3.4a
  // dialog (SaleCsAssignDialog) stays available on /admin/merchants where
  // the surface is meaningful.
}

interface AdminUsersResponse {
  users: AdminUserRow[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface SuccessEnvelope<T> {
  success: true;
  data: T;
  requestId: string;
}
interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string };
  requestId: string;
}

function unwrap<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as SuccessEnvelope<T>).success === true &&
    'data' in payload
  ) {
    return (payload as SuccessEnvelope<T>).data;
  }
  return payload as T;
}

function envelopeError(payload: unknown): string | null {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as ErrorEnvelope).success === false &&
    'error' in payload
  ) {
    return (payload as ErrorEnvelope).error?.message ?? null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Constants — mirrors GetAdminUsersQuery enums (API-CONTRACTS L2677-2680).
// ---------------------------------------------------------------------------

// Role enum values; display labels resolved via i18n
// (`admin_user.roleOptions.<value>`) at render time.
const ROLE_VALUES = [
  'superadmin',
  'admin',
  'support',
  'saler',
  'manager',
  'accountant',
  'affiliate',
] as const;

// Values match API enum (lowercase snake_case per UserStatus in
// apps/api/src/modules/auth/models/User.ts + GetAdminUsersListRequest at
// adminUsers.handler.ts:194). Display labels resolved via i18n
// (`admin_user.statusLabels.<value>`).
const STATUS_FILTER_VALUES = [
  'active',
  'pending_otp',
  'suspended',
  'banned',
  'deleted',
] as const;

const SORTABLE_FIELDS = ['createdAt', 'displayName', 'email', 'lastLoginAt'];

// ---------------------------------------------------------------------------
// Visual primitives — Round-15 (debug 2026-05-23) polish.
//
// Inlined here (not added to @printway/ui) since they're page-local: status
// pill, role chip, ID chip (yellow small), avatar (image or initials). If
// another admin list grows the same need, the primitives can be promoted to
// packages/ui then.
// ---------------------------------------------------------------------------

const STATUS_BADGE_STYLES: Record<string, string> = {
  active: 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300',
  pending_otp: 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300',
  pending_approval: 'border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/40 text-sky-800 dark:text-sky-300',
  pending_survey: 'border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/40 text-sky-800 dark:text-sky-300',
  suspended: 'border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/40 text-orange-800 dark:text-orange-300',
  banned: 'border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300',
  deleted: 'border-pw-border bg-pw-surface text-pw-text-muted',
  inactive: 'border-pw-border bg-pw-surface text-pw-text-muted',
};

// Statuses with a localized label under `admin_user.statusLabels.*`; any
// value outside this set falls back to the raw enum string.
const KNOWN_STATUSES: string[] = [
  'active',
  'pending_otp',
  'pending_approval',
  'pending_survey',
  'suspended',
  'banned',
  'deleted',
  'inactive',
];

function StatusBadge({ status }: { status: string }): JSX.Element {
  const { t } = useTranslation('users');
  const cls = STATUS_BADGE_STYLES[status] ?? STATUS_BADGE_STYLES['inactive'];
  const label = KNOWN_STATUSES.includes(status)
    ? t(`admin_user.statusLabels.${status}`)
    : status;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}
    >
      {label}
    </span>
  );
}

function RoleChip({ role }: { role: string }): JSX.Element {
  return (
    <span className="inline-flex items-center rounded-md border border-pw-border bg-pw-surface px-1.5 py-0.5 text-[11px] font-medium text-pw-text">
      {role}
    </span>
  );
}

/**
 * Chip for a TENANT account (merchant / vendor / affiliate). These accounts hold
 * no global platform role — `roles[]` is empty and grantRole() refuses to add
 * one — so the Role column used to show "—" for every merchant signup. Styled
 * differently from RoleChip (dashed border) so it reads as "account type", not
 * "granted platform role". dbg-20260730-072612.
 */
function AccountTypeChip({
  accountType,
  label,
}: {
  accountType: string;
  label: string;
}): JSX.Element {
  return (
    <span
      className="inline-flex items-center rounded-md border border-dashed border-pw-border bg-transparent px-1.5 py-0.5 text-[11px] font-medium text-pw-text-muted"
      data-testid={`admin-users-account-type-${accountType}`}
    >
      {label}
    </span>
  );
}

function IdChip({ id }: { id: string }): JSX.Element {
  // Short ID — last 6 chars of ObjectId is enough for visual disambiguation
  // without dragging row width. Full id stays in the title tooltip.
  const short = id.length > 6 ? id.slice(-6) : id;
  return (
    <span
      className="inline-flex items-center rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-1.5 py-[1px] font-mono text-[10px] font-medium text-amber-800 dark:text-amber-300"
      title={id}
    >
      #{short}
    </span>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

const AVATAR_PALETTE = [
  'bg-sky-100 dark:bg-sky-950/40 text-sky-800 dark:text-sky-300',
  'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300',
  'bg-violet-100 dark:bg-violet-950/40 text-violet-800 dark:text-violet-300',
  'bg-rose-100 dark:bg-rose-950/40 text-rose-800 dark:text-rose-300',
  'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300',
  'bg-teal-100 dark:bg-teal-950/40 text-teal-800 dark:text-teal-300',
  'bg-fuchsia-100 dark:bg-fuchsia-950/40 text-fuchsia-800 dark:text-fuchsia-300',
  'bg-indigo-100 dark:bg-indigo-950/40 text-indigo-800 dark:text-indigo-300',
];

function paletteFor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length]!;
}

function UserAvatar({
  url,
  name,
  id,
  size = 'md',
}: {
  url: string | null | undefined;
  name: string;
  id: string;
  size?: 'sm' | 'md';
}): JSX.Element {
  const dim = size === 'sm' ? 'h-7 w-7 text-[10px]' : 'h-9 w-9 text-[12px]';
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        className={`${dim} flex-shrink-0 rounded-full object-cover`}
        loading="lazy"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className={`flex ${dim} flex-shrink-0 items-center justify-center rounded-full font-semibold ${paletteFor(id)}`}
    >
      {initialsOf(name)}
    </span>
  );
}

// Round-16 — 2FA enrollment icon. Shield-check (green) when enabled,
// shield-off (muted) when disabled. Title attribute surfaces the state in
// hover for keyboard / screenreader users.
function TwoFactorIcon({ enabled }: { enabled: boolean }): JSX.Element {
  const { t } = useTranslation('users');
  if (enabled) {
    return (
      <span
        title={t('admin_user.twoFactor.enabledTitle')}
        className="inline-flex items-center gap-1 rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300"
      >
        <svg
          aria-hidden="true"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
        {t('admin_user.twoFactor.badgeOn')}
      </span>
    );
  }
  return (
    <span
      title={t('admin_user.twoFactor.disabledTitle')}
      className="inline-flex items-center gap-1 rounded-md border border-pw-border bg-pw-surface px-1.5 py-0.5 text-[11px] font-medium text-pw-text-muted"
    >
      <svg
        aria-hidden="true"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M19.69 14a6.9 6.9 0 0 0 .31-2V5l-8-3-3.16 1.18" />
        <path d="M4.73 4.73 4 5v7c0 6 8 10 8 10a20.29 20.29 0 0 0 5.62-4.38" />
        <path d="m2 2 20 20" />
      </svg>
      {t('admin_user.twoFactor.badgeOff')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sub-dialogs
// ---------------------------------------------------------------------------

interface RoleDialogState {
  user: AdminUserRow;
  mode: 'grant' | 'revoke';
}

function RolePickerDialog({
  state,
  onClose,
  onSubmit,
}: {
  state: RoleDialogState | null;
  onClose: () => void;
  onSubmit: (roleId: string, reason: string) => void | Promise<void>;
}): JSX.Element | null {
  const { t } = useTranslation('users');
  const [roleId, setRoleId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (state) {
      setRoleId('');
      setReason('');
      setSubmitting(false);
    }
  }, [state]);

  if (!state) return null;

  // Revoke mode lists ONLY the roles the user actually holds (you cannot
  // revoke a role they don't have). Grant mode offers the full catalog.
  const roleOptions: readonly string[] =
    state.mode === 'revoke'
      ? ROLE_VALUES.filter((r) => (state.user.roles ?? []).includes(r))
      : ROLE_VALUES;

  async function handleConfirm(): Promise<void> {
    if (!roleId || reason.trim().length < 10) return;
    setSubmitting(true);
    try {
      await onSubmit(roleId, reason.trim());
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogHeader>
        <DialogTitle>
          {state.mode === 'grant'
            ? t('admin_user.roleDialog.titleGrant', {
                name: state.user.displayName,
              })
            : t('admin_user.roleDialog.titleRevoke', {
                name: state.user.displayName,
              })}
        </DialogTitle>
      </DialogHeader>
      <div className="mt-4 space-y-3">
        <div>
          <label className="mb-1 block text-sm font-medium">
            {t('admin_user.roleDialog.roleLabel')}
          </label>
          <Select value={roleId} onValueChange={(value) => setRoleId(value)}>
            <SelectTrigger aria-label={t('admin_user.roleDialog.roleLabel')}>
              <SelectValue
                placeholder={t('admin_user.roleDialog.rolePlaceholder')}
              />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.length === 0 ? (
                <div className="px-2 py-1.5 text-sm text-pw-text-muted">
                  {t('admin_user.roleDialog.noRevocableRoles')}
                </div>
              ) : (
                roleOptions.map((r) => (
                  <SelectItem key={r} value={r}>
                    {t(`admin_user.roleOptions.${r}`)}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">
            {t('admin_user.roleDialog.reasonLabel')}
          </label>
          <textarea
            className="block h-20 w-full rounded-md border border-pw-border bg-pw-bg p-2 text-sm focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>
          {t('admin_user.roleDialog.cancel')}
        </Button>
        <Button
          variant={state.mode === 'revoke' ? 'destructive' : 'default'}
          onClick={handleConfirm}
          disabled={submitting || !roleId || reason.trim().length < 10}
        >
          {state.mode === 'grant'
            ? t('admin_user.roleDialog.grant')
            : t('admin_user.roleDialog.revoke')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Round-17 — User detail drawer (lightweight)
//
// Triggered by clicking the User cell. Shows the row's identity snapshot
// + quick-action shortcuts; for deeper info (permissions, activity log,
// sessions) we link to the existing /users/:id full page.
// ---------------------------------------------------------------------------

interface UserDetailDrawerProps {
  user: AdminUserRow | null;
  onClose: () => void;
  onAvatarClick: (url: string, name: string) => void;
}

function UserDetailDrawer({
  user,
  onClose,
  onAvatarClick,
}: UserDetailDrawerProps): JSX.Element | null {
  const { t } = useTranslation('users');
  // ESC key closes the drawer.
  useEffect(() => {
    if (!user) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [user, onClose]);

  if (!user) return null;

  const roleList = user.roles ?? [];
  const orderedRoles =
    roleList.length === 0
      ? [user.primaryRole].filter(Boolean)
      : user.primaryRole && roleList.includes(user.primaryRole)
        ? [user.primaryRole, ...roleList.filter((r) => r !== user.primaryRole)]
        : roleList;

  return (
    <div
      className="fixed inset-0 z-40 flex"
      role="dialog"
      aria-modal="true"
      aria-label={t('admin_user.drawer.aria', { name: user.displayName })}
      data-testid="admin-users-detail-drawer"
    >
      <button
        type="button"
        onClick={onClose}
        className="flex-1 bg-black/40" /* dark-ok: modal backdrop scrim, theme-agnostic (P999.11.D-04) */
        aria-label={t('admin_user.drawer.closeAria')}
        data-testid="admin-users-detail-backdrop"
      />
      <aside className="flex h-full w-full max-w-md flex-col gap-4 overflow-y-auto bg-pw-bg p-5 shadow-2xl">
        {/* Header — avatar + name + close */}
        <header className="flex items-start justify-between gap-3 border-b border-pw-border pb-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                if (user.avatarUrl) onAvatarClick(user.avatarUrl, user.displayName);
              }}
              disabled={!user.avatarUrl}
              className={`rounded-full ${user.avatarUrl ? 'cursor-zoom-in hover:opacity-80' : 'cursor-default'}`}
              aria-label={
                user.avatarUrl
                  ? t('admin_user.drawer.viewAvatarAria', {
                      name: user.displayName,
                    })
                  : ''
              }
              data-testid="admin-users-detail-avatar-btn"
            >
              {/* Bigger 14x14 avatar in the drawer */}
              <span
                aria-hidden="true"
                className={`flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-full ${user.avatarUrl ? '' : paletteFor(user.id) + ' text-[16px] font-semibold'}`}
              >
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.displayName}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  initialsOf(user.displayName)
                )}
              </span>
            </button>
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-pw-text">
                  {user.displayName}
                </h2>
                <IdChip id={user.id} />
              </div>
              <span className="text-sm text-pw-text-muted">{user.email}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-pw-border bg-pw-bg px-2.5 py-1 text-sm font-medium text-pw-text hover:bg-pw-surface"
            data-testid="admin-users-detail-close"
          >
            {t('admin_user.drawer.close')}
          </button>
        </header>

        {/* Field grid */}
        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <DrawerField label={t('admin_user.drawer.fields.status')}>
            <StatusBadge status={user.status} />
          </DrawerField>
          <DrawerField label={t('admin_user.drawer.fields.twoFactor')}>
            <TwoFactorIcon enabled={!!user.twoFactorEnabled} />
          </DrawerField>
          <DrawerField label={t('admin_user.drawer.fields.roles')}>
            {orderedRoles.length === 0 ? (
              // Tenant account (merchant/vendor/affiliate) — no global role by
              // design; show the account type. dbg-20260730-072612.
              user.accountType ? (
                <AccountTypeChip
                  accountType={user.accountType}
                  label={t(`admin_user.accountTypes.${user.accountType}`)}
                />
              ) : (
                <span className="text-pw-text-muted">—</span>
              )
            ) : (
              <div className="flex flex-wrap gap-1">
                {orderedRoles.map((r) => (
                  <RoleChip key={r} role={r} />
                ))}
              </div>
            )}
          </DrawerField>
          <DrawerField label={t('admin_user.drawer.fields.id')} mono>
            <span title={user.id}>{user.id}</span>
          </DrawerField>
          <DrawerField label={t('admin_user.drawer.fields.createdAt')}>
            {new Date(user.createdAt).toLocaleString()}
          </DrawerField>
          <DrawerField label={t('admin_user.drawer.fields.updatedAt')}>
            {user.updatedAt
              ? new Date(user.updatedAt).toLocaleString()
              : '—'}
          </DrawerField>
          <DrawerField label={t('admin_user.drawer.fields.lastLoginAt')}>
            {user.lastLoginAt
              ? new Date(user.lastLoginAt).toLocaleString()
              : t('admin_user.drawer.neverLoggedIn')}
          </DrawerField>
          <DrawerField label={t('admin_user.drawer.fields.avatarUrl')} mono>
            {user.avatarUrl ? (
              <a
                href={user.avatarUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="break-all text-pw-primary hover:underline"
              >
                {user.avatarUrl}
              </a>
            ) : (
              <span className="text-pw-text-muted">
                {t('admin_user.drawer.avatarFallback')}
              </span>
            )}
          </DrawerField>
        </dl>

        {/* Footer — link to the full detail page */}
        <footer className="mt-auto flex items-center justify-between border-t border-pw-border pt-3">
          <Link
            to={`/users/${encodeURIComponent(user.id)}`}
            className="text-sm font-medium text-pw-primary hover:underline"
            onClick={onClose}
            data-testid="admin-users-detail-full-link"
          >
            {t('admin_user.drawer.viewFull')}
          </Link>
          <span className="text-xs text-pw-text-muted">
            {t('admin_user.drawer.fullIncludes')}
          </span>
        </footer>
      </aside>
    </div>
  );
}

function DrawerField({
  label,
  children,
  mono = false,
}: {
  label: string;
  children: ReactNode;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[10px] uppercase tracking-wide text-pw-text-muted">
        {label}
      </dt>
      <dd
        className={`break-words text-pw-text ${mono ? 'font-mono text-xs' : 'text-sm'}`}
      >
        {children}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminUsersPage(): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('users');
  // need `user.id` to gate self-action buttons (cannot
  // edit/disable self). The Phase 1 implementation only used `permissions`;
  // the Phase 8.2 extension adds the current-user id read.
  const { permissions, user: currentUser } = useAuth();
  const currentUserId = currentUser?.id ?? null;
  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tableState, setTableState] = useState<TableState | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Action dialogs.
  const [confirmAction, setConfirmAction] = useState<
    | {
        user: AdminUserRow;
        kind: 'suspend' | 'unsuspend' | 'reset2fa';
      }
    | null
  >(null);
  const [confirmReason, setConfirmReason] = useState('');
  const [submittingAction, setSubmittingAction] = useState(false);
  const [roleDialog, setRoleDialog] = useState<RoleDialogState | null>(null);

  // Create dialog open state. Gated on `user:manage` or
  // `system:manage_admins` (the Phase 8.2 permission flag; either grant
  // unlocks the button per task-01 / namespace flattening).
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  // Wildcard-aware — superadmin holds `*:*` (not the literal keys), so a plain
  // `.includes()` hid the Create button for superadmin. Mirror sidebar canSee().
  const canCreateAdminUser = permissions.some(
    (g) =>
      g === PERMISSIONS.USER_MANAGE ||
      g === PERMISSIONS.MANAGE_ADMIN_USERS ||
      (g.includes('*') &&
        (permissionMatches(g, PERMISSIONS.USER_MANAGE) ||
          permissionMatches(g, PERMISSIONS.MANAGE_ADMIN_USERS))),
  );

  // Round-15 (debug 2026-05-23): Sale/CS dialog + filter dropdowns were
  // removed from this page since admin users (GlobalRoleType) never own a
  // Merchant doc. The assignment surface lives on /admin/merchants where
  // the row identity IS the merchant. SaleCsAssignDialog component stays
  // available for that consumer.

  // Round-17 (debug 2026-05-23) — click-to-preview surfaces:
  //   • User cell click → opens lightweight side drawer with the row's
  //     identity snapshot + quick-action shortcuts. Power users follow
  //     "Xem chi tiết đầy đủ →" link to the existing /users/:id page for
  //     permissions, activity, sessions deep-dive.
  //   • Avatar click → opens shared <ImageViewerModal> if the user has a
  //     custom avatarUrl. Falls back to no-op for initials-only chips.
  const [drawerUser, setDrawerUser] = useState<AdminUserRow | null>(null);
  const [avatarViewer, setAvatarViewer] = useState<{
    url: string;
    name: string;
  } | null>(null);

  const fetchUsers = useCallback(
    async (state: TableState): Promise<void> => {
      setLoading(true);
      try {
        const client = apiClient();
        const params = new URLSearchParams();
        params.set('page', String(state.page));
        params.set('limit', String(state.limit));
        if (state.search) params.set('search', state.search);
        const role = state.filters.role;
        const status = state.filters.status;
        // Bug 15 (debug-bill iter 9, 2026-05-04): BE schema GetAdminUsersQuery
        // expects `roleType` param (adminUsers.handler.ts:200), not `role`.
        // Previously FE sent `role` so BE silently dropped the unknown key
        // and returned all users — filter looked broken.
        if (role) params.set('roleType', role);
        if (status) params.set('status', status);
        const path = `/api/v1/admin/users?${params.toString()}`;
        const { data, error, response } = await (
          client as unknown as {
            GET: (
              p: string,
            ) => Promise<{
              data?: unknown;
              error?: unknown;
              response: Response;
            }>;
          }
        ).GET(path);
        if (error || !response.ok) {
          toast({
            title: t('admin_user.toast.loadFailTitle'),
            description: envelopeError(error) ?? `HTTP ${response.status}`,
            variant: 'error',
          });
          setRows([]);
          setTotal(0);
          return;
        }
        const payload = unwrap<AdminUsersResponse>(data);
        setRows(payload.users ?? []);
        setTotal(payload.pagination?.total ?? 0);
      } catch (err) {
        toast({
          title: t('admin_user.toast.networkErrorTitle'),
          description: err instanceof Error ? err.message : t('admin_user.toast.unknown'),
          variant: 'error',
        });
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [toast, t],
  );

  useEffect(() => {
    if (!tableState) return;
    void fetchUsers(tableState);
  }, [tableState, fetchUsers, refreshTick]);

  const filterableFields = useMemo<TableFilterField[]>(
    () => [
      {
        name: 'role',
        type: 'select',
        label: t('admin_user.filters.role'),
        placeholder: t('admin_user.filters.allRoles'),
        options: ROLE_VALUES.map((v) => ({
          value: v,
          label: t(`admin_user.roleOptions.${v}`),
        })),
      },
      {
        name: 'status',
        type: 'select',
        label: t('admin_user.filters.status'),
        placeholder: t('admin_user.filters.allStatuses'),
        options: STATUS_FILTER_VALUES.map((v) => ({
          value: v,
          label: t(`admin_user.statusLabels.${v}`),
        })),
      },
    ],
    [t],
  );

  // ---- Action handlers ----

  async function postWithReason(
    path: string,
    reason: string,
  ): Promise<{ ok: boolean; message?: string }> {
    const client = apiClient();
    const { error, response } = await (
      client as unknown as {
        POST: (
          p: string,
          init: { body: unknown },
        ) => Promise<{
          data?: unknown;
          error?: unknown;
          response: Response;
        }>;
      }
    ).POST(path, { body: { reason } });
    if (error || !response.ok) {
      return {
        ok: false,
        message: envelopeError(error) ?? `HTTP ${response.status}`,
      };
    }
    return { ok: true };
  }

  async function handleConfirmAction(): Promise<void> {
    if (!confirmAction) return;
    const { user, kind } = confirmAction;
    if (confirmReason.trim().length < 10) {
      toast({
        title: t('admin_user.confirmAction.reasonRequiredTitle'),
        description: t('admin_user.confirmAction.reasonRequiredDesc'),
        variant: 'warning',
      });
      return;
    }
    setSubmittingAction(true);
    try {
      let path: string;
      let successTitle: string;
      if (kind === 'suspend') {
        path = `/api/v1/admin/users/${encodeURIComponent(user.id)}/suspend`;
        successTitle = t('admin_user.toast.suspendedTitle');
      } else if (kind === 'unsuspend') {
        path = `/api/v1/admin/users/${encodeURIComponent(user.id)}/unsuspend`;
        successTitle = t('admin_user.toast.unsuspendedTitle');
      } else {
        path = `/api/v1/admin/users/${encodeURIComponent(user.id)}/reset-2fa`;
        successTitle = t('admin_user.toast.reset2faTitle');
      }
      const result = await postWithReason(path, confirmReason.trim());
      if (!result.ok) {
        toast({
          title: t('admin_user.toast.actionFailTitle'),
          description: result.message ?? t('admin_user.toast.unknownError'),
          variant: 'error',
        });
        return;
      }
      toast({
        title: successTitle,
        description: user.email,
        variant: 'success',
      });
      setConfirmAction(null);
      setConfirmReason('');
      setRefreshTick((t) => t + 1);
    } finally {
      setSubmittingAction(false);
    }
  }

  async function handleRoleSubmit(roleId: string, reason: string): Promise<void> {
    if (!roleDialog) return;
    const path =
      roleDialog.mode === 'grant'
        ? `/api/v1/admin/users/${encodeURIComponent(roleDialog.user.id)}/grant-role`
        : `/api/v1/admin/users/${encodeURIComponent(roleDialog.user.id)}/revoke-role`;
    const client = apiClient();
    const { error, response } = await (
      client as unknown as {
        POST: (
          p: string,
          init: { body: unknown },
        ) => Promise<{
          data?: unknown;
          error?: unknown;
          response: Response;
        }>;
      }
    // Bug fix (2026-05-03 dbg-grantrole): BE schema PostAdminUsersIdGrantRoleRequest
    // expects `{ roleType, reason? }`. The FE state var is named `roleId` for legacy
    // reasons but its value is the enum string (superadmin|admin|support|saler|...)
    // matching `GRANT_REVOKE_ROLE_TYPES`. Map FE local name → BE field name on wire.
    ).POST(path, { body: { roleType: roleId, reason } });
    if (error || !response.ok) {
      toast({
        title: t('admin_user.toast.roleUpdateFailTitle'),
        description: envelopeError(error) ?? `HTTP ${response.status}`,
        variant: 'error',
      });
      return;
    }
    toast({
      title:
        roleDialog.mode === 'grant'
          ? t('admin_user.toast.roleGrantedTitle')
          : t('admin_user.toast.roleRevokedTitle'),
      description: roleDialog.user.email,
      variant: 'success',
    });
    setRoleDialog(null);
    setRefreshTick((t) => t + 1);
  }

  // ---- Columns ----

  const columns = useMemo<TableColumn<AdminUserRow>[]>(
    () => [
      // Round-16 (debug 2026-05-23) — avatar split into its own dedicated
      // column. Round-17 — avatar becomes a button that opens the shared
      // ImageViewerModal when the row has a custom avatarUrl. Initials-
      // only avatars stay non-interactive (no image to view).
      {
        id: 'avatar',
        header: '',
        align: 'center',
        width: '56px',
        accessor: (row) => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (row.avatarUrl) {
                setAvatarViewer({ url: row.avatarUrl, name: row.displayName });
              }
            }}
            disabled={!row.avatarUrl}
            className={`rounded-full transition-opacity focus:outline-none focus:ring-2 focus:ring-pw-primary focus:ring-offset-2 ${row.avatarUrl ? 'cursor-zoom-in hover:opacity-80' : 'cursor-default'}`}
            aria-label={
              row.avatarUrl
                ? t('admin_user.avatar.viewAlt', { name: row.displayName })
                : ''
            }
            data-testid={`admin-users-row-${row.id}-avatar-btn`}
          >
            <UserAvatar
              url={row.avatarUrl}
              name={row.displayName}
              id={row.id}
              size="md"
            />
          </button>
        ),
      },
      // Round-17 — User cell becomes a button that opens the detail
      // drawer. Stops propagation so the existing action-cell buttons
      // (suspend/grant/...) keep firing without double-handling.
      {
        id: 'displayName',
        header: t('admin_user.columns.user'),
        sortable: true,
        accessor: (row) => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setDrawerUser(row);
            }}
            className="-mx-1 flex w-full min-w-0 flex-col gap-0.5 rounded-md px-1 py-1 text-left transition-colors hover:bg-pw-surface focus:bg-pw-surface focus:outline-none focus:ring-2 focus:ring-pw-primary focus:ring-offset-1"
            data-testid={`admin-users-row-${row.id}-open-drawer-btn`}
          >
            <div className="flex items-center gap-2">
              <span className="truncate font-medium text-pw-text">
                {row.displayName}
              </span>
              <IdChip id={row.id} />
            </div>
            <span className="truncate text-xs text-pw-text-muted">
              {row.email}
            </span>
          </button>
        ),
      },
      {
        id: 'primaryRole',
        header: t('admin_user.columns.roles'),
        // Display all granted roles as chip badges (additive grant-role
        // mutations append to `roles[]`, NOT primaryRole/roleType). Falls
        // back to primaryRole when older API doesn't emit the array.
        // Primary role stays first when present.
        accessor: (row) => {
          const list = row.roles ?? [];
          const ordered =
            list.length === 0
              ? [row.primaryRole].filter(Boolean)
              : row.primaryRole && list.includes(row.primaryRole)
                ? [row.primaryRole, ...list.filter((r) => r !== row.primaryRole)]
                : list;
          if (ordered.length === 0) {
            // No global role → tenant account (merchant/vendor/affiliate). Show
            // the account type instead of a bare "—" so the column is never
            // blank for merchant signups. dbg-20260730-072612.
            if (row.accountType) {
              return (
                <AccountTypeChip
                  accountType={row.accountType}
                  label={t(`admin_user.accountTypes.${row.accountType}`)}
                />
              );
            }
            return <span className="text-pw-text-muted">—</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {ordered.map((r) => (
                <RoleChip key={r} role={r} />
              ))}
            </div>
          );
        },
      },
      {
        id: 'status',
        header: t('admin_user.columns.status'),
        accessor: (row) => <StatusBadge status={row.status} />,
      },
      // Round-16 — 2FA enrollment column. Helps spot accounts that bypass
      // the security-policy nudge so admins can reach out / Reset 2FA on
      // suspect actors.
      {
        id: 'twoFactorEnabled',
        header: t('admin_user.columns.twoFactor'),
        align: 'center',
        accessor: (row) => <TwoFactorIcon enabled={!!row.twoFactorEnabled} />,
      },
      {
        id: 'createdAt',
        header: t('admin_user.columns.createdAt'),
        sortable: true,
        accessor: (row) => new Date(row.createdAt).toLocaleDateString(),
      },
      // Round-16 iter 2 — Updated column. Helps admins spot stale vs.
      // recently-touched accounts (profile edits, role grants, suspends,
      // 2FA resets all bump updatedAt). Full timestamp surfaces in the
      // title attribute on hover.
      {
        id: 'updatedAt',
        header: t('admin_user.columns.updated'),
        accessor: (row) =>
          row.updatedAt ? (
            <span title={new Date(row.updatedAt).toLocaleString()}>
              {new Date(row.updatedAt).toLocaleDateString()}
            </span>
          ) : (
            <span className="text-pw-text-muted">—</span>
          ),
      },
      {
        id: 'lastLoginAt',
        header: t('admin_user.columns.lastLogin'),
        sortable: true,
        accessor: (row) =>
          row.lastLoginAt ? (
            <span title={new Date(row.lastLoginAt).toLocaleString()}>
              {new Date(row.lastLoginAt).toLocaleDateString()}
            </span>
          ) : (
            <span className="text-pw-text-muted">—</span>
          ),
      },
      {
        id: 'actions',
        header: t('admin_user.columns.actions'),
        accessor: (row) => {
          // self-action guard. Disable mutation buttons when
          // the row represents the currently logged-in user so an admin
          // cannot lock themselves out (suspend self, revoke own admin
          // role, reset own 2FA). The title attribute surfaces the
          // reason on hover so the UX is discoverable, not silent.
          const isSelf =
            currentUserId !== null && row.id === currentUserId;
          const selfTitle = isSelf
            ? t('admin_user.rowActions.selfGuard')
            : undefined;
          return (
            <div className="flex flex-wrap gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRoleDialog({ user: row, mode: 'grant' })}
                disabled={isSelf}
                title={selfTitle}
                data-testid={`admin-users-row-${row.id}-grant-btn`}
              >
                {t('admin_user.rowActions.grant')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRoleDialog({ user: row, mode: 'revoke' })}
                disabled={isSelf}
                title={selfTitle}
                data-testid={`admin-users-row-${row.id}-revoke-btn`}
              >
                {t('admin_user.rowActions.revoke')}
              </Button>
              {row.status === 'suspended' ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setConfirmAction({ user: row, kind: 'unsuspend' });
                    setConfirmReason('');
                  }}
                  disabled={isSelf}
                  title={selfTitle}
                  data-testid={`admin-users-row-${row.id}-unsuspend-btn`}
                >
                  {t('admin_user.rowActions.unsuspend')}
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setConfirmAction({ user: row, kind: 'suspend' });
                    setConfirmReason('');
                  }}
                  disabled={isSelf}
                  title={selfTitle}
                  data-testid={`admin-users-row-${row.id}-suspend-btn`}
                >
                  {t('admin_user.rowActions.suspend')}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setConfirmAction({ user: row, kind: 'reset2fa' });
                  setConfirmReason('');
                }}
                disabled={isSelf}
                title={selfTitle}
                data-testid={`admin-users-row-${row.id}-reset-2fa-btn`}
              >
                {t('admin_user.rowActions.reset2fa')}
              </Button>
              {/* Impersonate is intentionally NOT gated by isSelf — the
                  ImpersonateButton component already filters out self-rows
                  internally per P1.D-36. Leaving the gating here would
                  double-mask and confuse the audit story. */}
              <ImpersonateButton user={row} />
            </div>
          );
        },
      },
    ],
    [currentUserId, t],
  );

  return (
    <main className="flex flex-col gap-4 p-6" data-testid="admin-users-page">
      {/* Create user CTA. Hidden when the operator lacks
          `user:manage` / `system:manage_admins` (D-99 acceptance: perm
          gate hides Create button). */}
      <PageHeader
        title={t('admin_user.page.heading')}
        subtitle={t('admin_user.page.subtitleWithTotal', { total })}
        actions={
          canCreateAdminUser && (
            <Button
              variant="default"
              data-testid="admin-users-create-btn"
              onClick={() => setCreateDialogOpen(true)}
            >
              {t('admin_user.actions.create')}
            </Button>
          )
        }
      />

      <Table<AdminUserRow>
        tableId="admin-users"
        columns={columns}
        data={rows}
        total={total}
        loading={loading}
        sortableFields={SORTABLE_FIELDS}
        filterableFields={filterableFields}
        paginationMode="offset"
        pageSize={20}
        defaultSortBy="createdAt"
        defaultSortOrder="desc"
        searchPlaceholder={t('admin_user.search.placeholder')}
        emptyState={t('admin_user.empty.title')}
        onStateChange={setTableState}
      />

      {/* Confirm dialog with reason input. */}
      {confirmAction && (
        <ConfirmDialog
          open={!!confirmAction}
          onOpenChange={(o) => !o && setConfirmAction(null)}
          type={confirmAction.kind === 'unsuspend' ? 'neutral' : 'destructive'}
          title={
            confirmAction.kind === 'suspend'
              ? t('admin_user.confirmAction.suspendTitle', {
                  name: confirmAction.user.displayName,
                })
              : confirmAction.kind === 'unsuspend'
                ? t('admin_user.confirmAction.unsuspendTitle', {
                    name: confirmAction.user.displayName,
                  })
                : t('admin_user.confirmAction.reset2faTitle', {
                    name: confirmAction.user.displayName,
                  })
          }
          description={
            <div className="space-y-2 text-left">
              <p>
                {confirmAction.kind === 'suspend'
                  ? t('admin_user.confirmAction.suspendDesc')
                  : confirmAction.kind === 'unsuspend'
                    ? t('admin_user.confirmAction.unsuspendDesc')
                    : t('admin_user.confirmAction.reset2faDesc')}
              </p>
              <Input
                placeholder={t('admin_user.confirmAction.reasonPlaceholder')}
                value={confirmReason}
                onChange={(e) => setConfirmReason(e.target.value)}
              />
            </div>
          }
          confirmLabel={t('admin_user.confirmAction.confirmLabel')}
          loading={submittingAction}
          onConfirm={handleConfirmAction}
        />
      )}

      <RolePickerDialog
        state={roleDialog}
        onClose={() => setRoleDialog(null)}
        onSubmit={handleRoleSubmit}
      />

      {/* Phase 8.2 Wave 7 Task 49 — Create admin user dialog.
          Mounts only when the operator has permission to create (gate is
          also enforced on the header button); the dialog re-checks via
          BE 403 if the perm was revoked mid-session. */}
      {canCreateAdminUser && (
        <CreateAdminUserDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onCreated={() => {
            // Refresh the legacy fetcher (Phase 1 useEffect path) so the
            // new row appears without requiring TanStack invalidation —
            // the dialog already invalidates `adminUserKeys.lists()`
            // upstream for any future migration of this page to RQ.
            setRefreshTick((t) => t + 1);
          }}
        />
      )}

      {/* Round-17 — lightweight detail drawer (User-cell click). Renders
          the row snapshot we already have in memory + a footer link to
          /users/:id for permissions/activity/sessions deep-dive. */}
      <UserDetailDrawer
        user={drawerUser}
        onClose={() => setDrawerUser(null)}
        onAvatarClick={(url, name) => setAvatarViewer({ url, name })}
      />

      {/* Round-17 — shared image viewer for avatar click. Falls back to
          a single-image gallery; the modal handles ESC + backdrop close
          internally. */}
      <ImageViewerModal
        open={avatarViewer !== null}
        images={avatarViewer ? [avatarViewer.url] : []}
        alt={avatarViewer?.name ?? ''}
        onClose={() => setAvatarViewer(null)}
      />
    </main>
  );
}
